import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, CheckCircle2, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { sections as fallbackSections } from "./data";
import { AssessmentSidebar } from "./Sidebar";
import { QuestionCard } from "./QuestionCard";
import { CompanyForm } from "./CompanyForm";
import { AssessmentHero } from "./AssessmentHero";
import type { CompanyDetails, Section } from "./types";
import { getQuestions, submitAssessment } from "../../api";

const mapApiQuestionsToSections = (apiData: any): Section[] => {
  if (!apiData || !Array.isArray(apiData.pillar)) return fallbackSections;

  return apiData.pillar.map((pillar: any) => ({
    id: String(pillar.id),
    shortLabel: String(pillar.title || pillar.id || "Section"),
    title: String(pillar.title || "Section"),
    intro: undefined,
    questions: Array.isArray(pillar.questions)
      ? pillar.questions.map((q: any) => ({
          id: String(q.id),
          label: String(q.question || "Question"),
          options: q.options
            ? Object.entries(q.options).map(([label, value]) => ({
                label,
                value: Number(value),
              }))
            : [],
        }))
      : [],
  }));
};

const initialCompany: CompanyDetails = {
  companyName: "",
  email: "",
  industry: "",
  jobTitle: "",
  employeeSize: "",
  website: "",
  phone: "",
};

const isValidEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());

export const SignalAssessment = () => {
  const navigate = useNavigate();
  const assessmentRef = useRef<HTMLElement | null>(null);
  const [sections, setSections] = useState<Section[]>(fallbackSections);
  const [step, setStep] = useState(0);
  const [company, setCompany] = useState<CompanyDetails>(initialCompany);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>("");
  const [loadingQuestions, setLoadingQuestions] = useState(true);

  const scrollToAssessment = () => {
    const el = assessmentRef.current;
    if (!el) return;
    window.scrollTo({ top: el.offsetTop, behavior: "smooth" });
  };

  useEffect(() => {
    const loadQuestions = async () => {
      try {
        setLoadingQuestions(true);

        const data = await getQuestions();

        if (data && Array.isArray(data.pillar)) {
          setSections(mapApiQuestionsToSections(data));
          return;
        }

        setSections(fallbackSections);
      } catch (err) {
        console.error("Error loading questions:", err);
        setSections(fallbackSections);
      } finally {
        setLoadingQuestions(false);
      }
    };

    loadQuestions();
  }, []);


  // Add a review/submit step after last section
  const TOTAL_STEPS = sections.length + 2; // company details + sections + review/submit

  const isCompanyStep = step === 0;
  const isReview = step === sections.length + 1;
  const currentSection = step > 0 && step <= sections.length ? sections[step - 1] : null;

  const progress = useMemo(() => {
    return Math.round((step / (TOTAL_STEPS - 1)) * 100);
  }, [step, TOTAL_STEPS]);

  const canContinue = useMemo(() => {
    if (isCompanyStep) {
      return Boolean(
        company.companyName &&
          company.email &&
          isValidEmail(company.email) &&
          company.industry &&
          company.jobTitle &&
          company.employeeSize,
      );
    }
    if (loadingQuestions) return false;
    if (isReview) return true;
    if (!currentSection) return false;
    return currentSection.questions.every((q) => answers[q.id]);
  }, [isCompanyStep, company, loadingQuestions, isReview, currentSection, answers]);

  const handleSubmit = async () => {
    if (submitting) return;

    setSubmitting(true);
    setSubmitError("");

    const result = await submitAssessment(
      {
        companyName: company.companyName,
        email: company.email,
        industry: company.industry,
        jobTitle: company.jobTitle,
        employeeSize: company.employeeSize,
        website: company.website,
        phone: company.phone,
      },
      answers,
    );

    if (!result?.assessment_id) {
      setSubmitError("Submission failed. Please try again.");
      setSubmitting(false);
      return;
    }

    setSubmitting(false);

    navigate("/signalai-user-details", {
      state: {
        company: {
          company_name: company.companyName,
          email: company.email,
          industry: company.industry,
          designation: company.jobTitle,
          size: company.employeeSize,
          website: company.website,
          phone: company.phone,
        },
        answers,
        assessment_id: result.assessment_id,
      },
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="signal-assessment-landing">
        <AssessmentHero onStart={scrollToAssessment} />
      </div>

      <section
        id="assessment"
        ref={assessmentRef}
        className="relative min-h-screen bg-gradient-hero py-10 sm:py-14 overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-mesh opacity-60 pointer-events-none" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-start gap-8 lg:grid-cols-[340px_1fr] lg:gap-10">
            {/* Sidebar: Why this matters, progress, info box */}
            <AssessmentSidebar
              sections={sections}
              currentIndex={Math.min(step, sections.length)}
              progress={progress}
            />

            {/* Main content: Why this matters + form/card */}
            <div className="min-w-0">
              {/* Why this matters box - aligned with header on the left */}
              <div className="mb-6 rounded-2xl border border-[#122c54] bg-[linear-gradient(135deg,#060f22_0%,#081329_48%,#0b1b36_100%)] px-5 py-4 shadow-[0_18px_40px_-24px_rgba(5,15,40,0.85)]">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.24em] text-[#1fb6ff]">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                  Why this matters
                </div>
                <p className="mt-2.5 text-xm leading-relaxed text-white/90">
                  Handling <b>~6,000 invoices/month masked ~15% revenue leakage</b> across delays, rework, and missed discounts. <b>Focusing on high-ROI levers</b> and not blanket automation <b>- unlocked 8-12% in working capital gains and faster revenue realization.</b>
                </p>
              </div>
              <div className="rounded-3xl border border-border bg-white p-6 shadow-elevated sm:p-8 lg:p-9">
                {/* Step header */}
                <div className="mb-7 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    {isCompanyStep && (
                      <p className="text-base font-semibold uppercase tracking-[0.18em] text-primary sm:text-[1.05rem]">
                        STEP 1 OF {TOTAL_STEPS} · COMPANY
                      </p>
                    )}
                    {/* Removed Company Details title as per request */}
                    {isCompanyStep && (
                      <p className="mt-3 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-[1.1rem]">
                        A few details so we can tailor the readiness review to your context.
                      </p>
                    )}
                    {!isCompanyStep && (
                      <div className="text-xl sm:text-2xl font-bold tracking-tight text-primary">
                        {`STEP ${step + 1} OF ${TOTAL_STEPS} · ${isReview ? "REVIEW" : currentSection?.shortLabel?.toUpperCase()}`}
                      </div>
                    )}
                    {!isCompanyStep && !isReview && currentSection!.intro && (
                      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
                        {currentSection!.intro}
                      </p>
                    )}
                  </div>
                </div>

                {/* Body */}
                <div className="space-y-8">
                  {/* Removed loading questions banner as per request */}

                  {/* Removed API error fallback banner as per request */}

                  {submitError && (
                    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                      {submitError}
                    </div>
                  )}

                  {isCompanyStep && (
                    <CompanyForm values={company} onChange={setCompany} disabled={submitting} />
                  )}

                  {!isCompanyStep && !isReview && (
                    <div className="space-y-8">
                      {currentSection!.questions.map((q, i) => (
                        <QuestionCard
                          key={q.id}
                          index={i + 1}
                          question={q}
                          value={answers[q.id]}
                          disabled={submitting}
                          onChange={(v) =>
                            setAnswers((prev) => ({ ...prev, [q.id]: v }))
                          }
                        />
                      ))}
                    </div>
                  )}

                  {isReview && <ReviewBlock />}
                </div>

                {/* Footer nav */}
                <div className="mt-10 flex items-center justify-between gap-4 border-t border-border pt-6">
                  <button
                    type="button"
                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                    disabled={step === 0}
                    className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40 disabled:hover:text-muted-foreground"
                  >
                    <ArrowLeft className="h-4 w-4" /> Back
                  </button>

                  {isReview ? (
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={submitting}
                      className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-[var(--shadow-card-hover)] transition-transform hover:-translate-y-0.5"
                    >
                      <Send className="h-4 w-4" />
                      {submitting ? "Submitting..." : "Get My Signal AI View"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setStep((s) => Math.min(TOTAL_STEPS - 1, s + 1))}
                      disabled={!canContinue}
                      className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-all hover:-translate-y-0.5 hover:shadow-[var(--shadow-card-hover)] disabled:translate-y-0 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground disabled:shadow-none"
                    >
                      {step === sections.length ? "Review & Submit" : <>Continue <ArrowRight className="h-4 w-4" /></>}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const ReviewBlock = () => (
  <div className="space-y-6">
    <p className="max-w-2xl text-base leading-relaxed text-muted-foreground">
      Your responses will be reviewed to identify readiness signals and where
      AI can create measurable ROI.
    </p>
    <ul className="grid gap-3 sm:grid-cols-2">
      {[
        "Reviewed by senior AI consultants",
        "Tailored to your industry & scale",
        "Practical ROI-focused recommendations",
        "No generic auto-generated score",
      ].map((t) => (
        <li
          key={t}
          className="flex items-start gap-3 rounded-xl border border-border bg-[hsl(var(--primary-soft))]/40 p-4"
        >
          <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-primary" />
          <span className="text-sm text-foreground">{t}</span>
        </li>
      ))}
    </ul>
    <p className="text-xs text-muted-foreground">
      No cost to begin. You decide next steps after clarity.
    </p>
  </div>
);

export default SignalAssessment;
